/**
 * Cadrul aplicatiei: antet, navigatie, subsol, bara de cos.
 *
 * Antetul are trei lucruri, in ordinea in care le cauta clientul unei platforme
 * de livrare: unde livram (pastila de adresa, deschide fereastra de adresa),
 * cine suntem (numele/logo-ul, duce acasa) si cosul. Navigatia catre paginile
 * pietei si catre comenzi sta la dreapta; pe telefon, paginile in plus intra
 * intr-un meniu.
 *
 * Navigatia vine din `config.layout.tabs` (ADR-011: doar scalari in config;
 * rutele sunt in cod). O cheie necunoscuta se ignora — un tab pe care bundle-ul
 * asta nu il cunoaste ar duce la o pagina inexistenta, iar piata pinuita pe o
 * versiune mai veche a web-ului e un caz normal, nu o eroare (ADR-002).
 */

import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, NavLink, useLocation, type To } from "react-router-dom";

import type { MarketPageMeta } from "@deliveryapp/shared";

import { useAddressSheet } from "./AddressSheetProvider";
import { formatAddressShort } from "./AddressForm";
import { useBootstrap } from "./BootstrapProvider";
import { readRememberedOrders, useCart } from "./CartProvider";
import { IconCart, IconChevronDown, IconMenu, IconPin, IconReceipt } from "./icons";
import { Price } from "./ui";

/**
 * Publica inaltimea REALA a antetului in `--da-header-h`.
 *
 * Antetul nu are inaltime fixa: pastila de adresa se muta pe al doilea rand pe
 * ecran ingust, logoul poate lipsi, comutatorul de limba apare doar la piete
 * multilingve. Tot ce se lipeste sub el — bara de categorii — si tot ce sare la
 * o ancora citesc variabila asta.
 */
function usePublishedHeaderHeight(): React.RefObject<HTMLElement> {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const header = ref.current;
    if (header === null) return;
    const publish = (): void => {
      const height = header.getBoundingClientRect().height;
      if (height > 0) document.documentElement.style.setProperty("--da-header-h", `${Math.round(height)}px`);
    };
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(header);
    window.addEventListener("resize", publish);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", publish);
    };
  }, []);

  return ref;
}

/**
 * Publica inaltimea barei lipite JOS in `--da-bottom-bar-h`, sau 0 cand nu e.
 * Notificarile si continutul paginii se aseaza deasupra ei.
 */
function usePublishedBottomBarHeight(visible: boolean): React.RefObject<HTMLDivElement> {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = document.documentElement;
    const bar = ref.current;
    if (!visible || bar === null) {
      root.style.setProperty("--da-bottom-bar-h", "0px");
      return;
    }
    const publish = (): void => {
      root.style.setProperty("--da-bottom-bar-h", `${Math.round(bar.getBoundingClientRect().height)}px`);
    };
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(bar);
    return () => {
      observer.disconnect();
      root.style.setProperty("--da-bottom-bar-h", "0px");
    };
  }, [visible]);

  return ref;
}

interface TabDefinition {
  key: string;
  fallbackLabel: string;
  to: To;
}

const TAB_ROUTES: Record<string, { fallbackLabel: string; to: To }> = {
  home: { fallbackLabel: "Acasă", to: "/" },
  storefronts: { fallbackLabel: "Restaurante", to: "/" },
  cart: { fallbackLabel: "Coș", to: "/cos" },
  // `orders` duce la lista comenzilor retinute local. Nu exista cont: singurul
  // lucru pe care il stie browserul sunt comenzile pe care le-a plasat el. Cand
  // nu exista niciuna, tabul nu se afiseaza deloc.
  orders: { fallbackLabel: "Comenzile mele", to: "/comenzi" },
  legal: { fallbackLabel: "Termeni și confidențialitate", to: "/legal" },
};

function buildTabs(tabs: string[], labels: Record<string, string> | undefined): TabDefinition[] {
  const source = tabs.length > 0 ? tabs : ["home", "cart", "legal"];
  const result: TabDefinition[] = [];
  for (const key of source) {
    const route = TAB_ROUTES[key];
    if (route === undefined) continue;
    result.push({ key, fallbackLabel: labels?.[key] ?? route.fallbackLabel, to: route.to });
  }
  return result;
}

function buildPageTabs(pages: MarketPageMeta[] | undefined): TabDefinition[] {
  if (!Array.isArray(pages)) return [];
  return pages
    .filter((page) => page.slug.trim().length > 0 && page.showInNav !== false)
    .map((page) => ({
      key: `pagina:${page.slug}`,
      fallbackLabel: page.navLabel ?? page.title,
      to: `/p/${encodeURIComponent(page.slug.trim().toLowerCase())}`,
    }));
}

const LOCALE_LABELS: Record<string, string> = {
  ro: "Română",
  en: "English",
  hu: "Magyar",
  de: "Deutsch",
  fr: "Français",
  it: "Italiano",
};

/**
 * Duce ecranul la ancora din adresa (`/#vitrine`). Aplicatia e un SPA: navigarea
 * catre `#ceva` schimba adresa prin ruter si NU declanseaza derularea nativa.
 */
function useHashScroll(hash: string, pathname: string): void {
  useEffect(() => {
    const id = hash.startsWith("#") ? hash.slice(1) : "";
    if (id.length === 0) { window.scrollTo(0, 0); return; }
    const frame = window.requestAnimationFrame(() => {
      let decoded = id;
      try { decoded = decodeURIComponent(id); } catch { /* Keep malformed fragments inert. */ }
      const target = document.getElementById(decoded);
      target?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [hash, pathname]);
}

const NAV_LINK = "da-press rounded-control px-2.5 py-2 text-sm font-semibold sm:px-3";

export function Layout({ children }: { children: ReactNode }) {
  const { config, marketKey, bootstrap, locale, availableLocales, setLocale } = useBootstrap();
  const { session, itemCount, subtotal } = useCart();
  const location = useLocation();
  useHashScroll(location.hash, location.pathname);
  const addressSheet = useAddressSheet();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const headerRef = usePublishedHeaderHeight();
  const hasOrders = readRememberedOrders(marketKey).length > 0;
  const selectableLocales = availableLocales.filter((item) => LOCALE_LABELS[item] !== undefined);
  const fixedTabs = buildTabs(config.layout.tabs ?? [], config.layout.tabLabels).filter(
    (tab) => tab.key !== "orders" || hasOrders,
  );
  const pageTabs = buildPageTabs(config.layout.pages);
  const inlinePages = pageTabs.length <= 4 && pageTabs.reduce((size, tab) => size + tab.fallbackLabel.length, 0) <= 48;
  const homeTab = fixedTabs.find((tab) => tab.key === "home" || tab.key === "storefronts") ?? null;
  const ordersTab = fixedTabs.find((tab) => tab.key === "orders") ?? null;
  const showCartTab = fixedTabs.some((tab) => tab.key === "cart");

  const path = location.pathname;
  useEffect(() => {
    let slug: string | null = path === "/" ? "" : null;
    if (path.startsWith("/p/")) { try { slug = decodeURIComponent(path.slice(3)).toLowerCase(); } catch { /* Unknown page. */ } }
    const page = (config.layout.pages ?? []).find(page => page.slug === slug);
    const name = config.identity.name || bootstrap.marketName;
    document.title = page && slug ? `${page.title} · ${name}` : config.seo?.titleTemplate?.replace("%s", name) || name;
    const description = page?.description || config.seo?.description || "";
    let tag = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (description) {
      if (!tag) { tag = document.createElement("meta"); tag.name = "description"; document.head.appendChild(tag); }
      tag.content = description;
    } else tag?.remove();
  }, [path, config, bootstrap.marketName]);
  const onCheckoutFlow = path.startsWith("/finalizare") || path.startsWith("/cos");
  const onTracking = path.startsWith("/comanda/");
  // Pe cos si la finalizare, totalul e deja pe ecran: bara ar dubla informatia
  // si ar acoperi butonul de confirmare.
  const showCartBar = itemCount > 0 && !onCheckoutFlow && !onTracking;
  const bottomBarRef = usePublishedBottomBarHeight(showCartBar);
  // Pastila de adresa: peste tot unde adresa schimba ce se vede. La finalizare si
  // la urmarire adresa e in pagina, iar o a doua cale de a o schimba ar deruta.
  const showAddressPill = !path.startsWith("/finalizare") && !onTracking && !path.startsWith("/legal");
  const address = session.address;

  // Meniul mobil se inchide la orice navigare.
  useEffect(() => {
    setMenuOpen(false);
  }, [path, location.hash]);
  useEffect(() => {
    if (!menuOpen) return;
    const closeOutside = (event: PointerEvent) => {
      if (event.target instanceof Node && !menuRef.current?.contains(event.target)) setMenuOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setMenuOpen(false);
      menuRef.current?.querySelector<HTMLButtonElement>("button")?.focus();
    };
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", escape);
    return () => { document.removeEventListener("pointerdown", closeOutside); document.removeEventListener("keydown", escape); };
  }, [menuOpen]);

  return (
    <div className="flex min-h-screen flex-col bg-canvas text-ink">
      <a className="da-skip-link" href="#continut">
        Sari la conținut
      </a>

      <header ref={headerRef} className="sticky top-0 z-30 border-b border-line bg-canvas/95 backdrop-blur">
        <div className="mx-auto flex max-w-app flex-wrap items-center gap-x-2 gap-y-1.5 px-3 py-2 sm:flex-nowrap sm:gap-4 sm:px-4">
          <Link to="/" className="flex min-w-0 max-w-[52%] items-center gap-2 text-lg font-extrabold tracking-tight text-ink sm:max-w-[15rem]">
            {config.branding.logoUrl === undefined || config.branding.logoUrl.length === 0 ? null : (
              <img src={config.branding.logoUrl} alt="" className="h-8 w-auto max-w-16 shrink-0 object-contain sm:max-w-24" />
            )}
            <span className="min-w-0 truncate">{config.identity.name || bootstrap.marketName}</span>
          </Link>

          {/* Pastila de adresa: pe ecran mare intre nume si navigatie; pe telefon
              trece pe al doilea rand, pe toata latimea. */}
          {showAddressPill ? (
            <button
              type="button"
              onClick={() => addressSheet.open()}
              className="da-press order-last flex w-full min-w-0 items-center gap-2 rounded-full border border-line bg-surface px-3 py-2 text-left hover:border-brand sm:order-none sm:w-auto sm:max-w-xs sm:flex-1"
              aria-label={address === null ? "Adaugă adresa de livrare" : `Adresa de livrare: ${formatAddressShort(address)}, ${address.city}. Schimbă`}
            >
              <IconPin size={18} className="shrink-0 text-brand-on-surface" />
              <span className="min-w-0 flex-1 leading-tight">
                <span className="block text-[11px] font-semibold uppercase tracking-wide text-ink-muted">
                  {address === null ? "Livrare la" : "Livrăm la"}
                </span>
                <span className="da-clamp-1 block text-sm font-bold text-ink">
                  {address === null ? "Adaugă adresa" : `${formatAddressShort(address)}, ${address.city}`}
                </span>
              </span>
              <IconChevronDown size={16} className="shrink-0 text-ink-muted" />
            </button>
          ) : null}

          <nav aria-label="Navigare principală" className="ml-auto flex shrink-0 items-center gap-0.5 sm:gap-1">
            {!onCheckoutFlow && !onTracking && !path.startsWith("/vitrina/") && path !== "/meniu" ? (
              <Link to="/meniu" className="da-tap inline-flex items-center rounded-control bg-brand px-3 py-2 text-sm font-bold text-brand-contrast hover:bg-brand-hover">Comandă</Link>
            ) : null}
            {homeTab === null ? null : (
              <NavLink
                to={homeTab.to}
                end
                className={({ isActive }) => `${NAV_LINK} hidden xl:inline-flex ${isActive ? "bg-brand-soft text-brand-on-surface" : "text-ink-muted hover:text-ink"}`}
              >
                {homeTab.fallbackLabel}
              </NavLink>
            )}
            {(inlinePages ? pageTabs : []).map((tab) => (
              <NavLink
                key={tab.key}
                to={tab.to}
                className={({ isActive }) => `${NAV_LINK} hidden xl:inline-flex ${isActive ? "bg-brand-soft text-brand-on-surface" : "text-ink-muted hover:text-ink"}`}
              >
                {tab.fallbackLabel}
              </NavLink>
            ))}
            {ordersTab === null ? null : (
              <NavLink
                to={ordersTab.to}
                className={({ isActive }) => `${NAV_LINK} inline-flex items-center gap-1.5 ${isActive ? "bg-brand-soft text-brand-on-surface" : "text-ink-muted hover:text-ink"}`}
                aria-label={ordersTab.fallbackLabel}
              >
                <IconReceipt size={20} />
                <span className="hidden md:inline">{ordersTab.fallbackLabel}</span>
              </NavLink>
            )}
            {showCartTab ? (
              <NavLink
                to="/cos"
                className={({ isActive }) => `${NAV_LINK} relative inline-flex items-center gap-1.5 ${isActive ? "bg-brand-soft text-brand-on-surface" : "text-ink-muted hover:text-ink"}`}
                aria-label={itemCount > 0 ? `Coș, ${itemCount} produse` : "Coș"}
              >
                <IconCart size={22} />
                <span className="hidden md:inline">Coș</span>
                {itemCount > 0 ? (
                  <span className="absolute -right-0.5 -top-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-brand px-1 text-[11px] font-extrabold text-brand-contrast">
                    {itemCount}
                  </span>
                ) : null}
              </NavLink>
            ) : null}

            {pageTabs.length === 0 && selectableLocales.length < 2 ? null : (
              <div ref={menuRef} className={`relative ${inlinePages ? "xl:hidden" : ""}`}>
                <button
                  type="button"
                  onClick={() => setMenuOpen((open) => !open)}
                  aria-expanded={menuOpen}
                  aria-controls="pagini-website"
                  aria-label="Meniu"
                  className={`${NAV_LINK} inline-flex items-center text-ink-muted hover:text-ink`}
                >
                  <IconMenu size={22} />
                  {!inlinePages && <span className="ml-1 hidden xl:inline">Pagini</span>}
                </button>
                {menuOpen ? (
                  <div id="pagini-website" className="absolute right-0 top-[calc(100%+0.5rem)] z-50 flex max-h-[65dvh] w-64 max-w-[calc(100vw-2rem)] flex-col gap-1 overflow-y-auto rounded-card border border-line bg-canvas p-2 shadow-lg">
                    {homeTab === null ? null : (
                      <NavLink to={homeTab.to} end className={({ isActive }) => `${NAV_LINK} ${isActive ? "bg-brand-soft text-brand-on-surface" : "text-ink hover:bg-surface"}`}>
                        {homeTab.fallbackLabel}
                      </NavLink>
                    )}
                    {pageTabs.map((tab) => (
                      <NavLink key={`mobil-${tab.key}`} to={tab.to} className={({ isActive }) => `${NAV_LINK} ${isActive ? "bg-brand-soft text-brand-on-surface" : "text-ink hover:bg-surface"}`}>
                        {tab.fallbackLabel}
                      </NavLink>
                    ))}
                    {selectableLocales.length < 2 ? null : (
                      <label className="flex items-center gap-2 px-2.5 py-2 text-sm text-ink-muted">
                        <span>Limba</span>
                        <select className="rounded-control border border-line bg-canvas px-2 py-1.5 text-sm text-ink" value={locale} onChange={(event) => setLocale(event.target.value)}>
                          {selectableLocales.map((item) => (
                            <option key={item} value={item}>{LOCALE_LABELS[item]}</option>
                          ))}
                        </select>
                      </label>
                    )}
                  </div>
                ) : null}
              </div>
            )}

            {selectableLocales.length < 2 || !selectableLocales.includes(locale) ? null : (
              <label className="ml-1 hidden items-center gap-1 text-sm text-ink-muted sm:flex">
                <span className="sr-only">Limba</span>
                <select
                  className="rounded-control border border-line bg-canvas px-2 py-1.5 text-sm text-ink"
                  value={locale}
                  onChange={(event) => setLocale(event.target.value)}
                >
                  {selectableLocales.map((item) => (
                    <option key={item} value={item}>
                      {LOCALE_LABELS[item]}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </nav>
        </div>
      </header>

      <main
        id="continut"
        className="mx-auto w-full max-w-app flex-1 px-4 py-5 sm:py-6"
        style={{ paddingBottom: showCartBar ? "calc(var(--da-bottom-bar-h) + 1.5rem)" : undefined }}
      >
        {children}
      </main>

      {/* Bara de cos: pe TOATE ecranele, cat timp exista produse si nu suntem
          deja in cos sau la finalizare. `z-40` o tine peste antetul lipit. */}
      {showCartBar ? (
        <div ref={bottomBarRef} className="da-bottom-bar">
          <div className="mx-auto max-w-app px-4 py-3">
            <Link
              to="/cos"
              className="da-press da-tap flex items-center gap-3 rounded-control bg-brand px-4 py-3 font-bold text-brand-contrast shadow-md hover:bg-brand-hover"
            >
              <span className="grid h-7 min-w-7 place-items-center rounded-full bg-brand-contrast/20 px-2 text-sm tabular-nums">{itemCount}</span>
              <span className="min-w-0 flex-1 leading-tight">
                <span className="block">Vezi coșul</span>
                {session.storefrontName === null ? null : (
                  <span className="da-clamp-1 block text-xs font-medium opacity-85">{session.storefrontName}</span>
                )}
              </span>
              <Price amount={subtotal} className="text-base" />
            </Link>
          </div>
        </div>
      ) : null}

      <footer className="border-t border-line bg-surface">
        <div className="mx-auto flex max-w-app flex-col gap-2 px-4 py-6 text-sm text-ink-muted">
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <Link className="underline" to="/legal">
              Termeni și condiții
            </Link>
            <Link className="underline" to="/legal/confidentialitate">
              Confidențialitate
            </Link>
            {hasOrders ? (
              <Link className="underline" to="/comenzi">
                Comenzile mele
              </Link>
            ) : null}
            {config.identity.supportPhone === undefined ? null : (
              <a className="underline" href={`tel:${config.identity.supportPhone.replace(/\s+/g, "")}`}>
                {`Asistență: ${config.identity.supportPhone}`}
              </a>
            )}
            {config.identity.supportEmail === undefined ? null : (
              <a className="underline" href={`mailto:${config.identity.supportEmail}`}>
                {config.identity.supportEmail}
              </a>
            )}
          </div>
          {config.layout.footerNote === undefined || config.layout.footerNote.length === 0 ? null : (
            <p>{config.layout.footerNote}</p>
          )}
          {config.identity.legalNotice === undefined ? null : <p>{config.identity.legalNotice}</p>}
          <p className="text-xs">
            {`Versiune aplicație ${bootstrap.appVersion} · configurație ${bootstrap.configVersion}`}
          </p>
        </div>
      </footer>

    </div>
  );
}
